// 2026-09-17：管理表格按真实容器分配列宽，父子表与左右固定偏移共用同一个模型。
import { compactColumnWidth } from './compactList'

export interface AdaptiveColumn {
  key?: string | number; dataIndex?: string | string[]; title?: unknown; width?: number | string;
  minWidth?: number; maxWidth?: number; flex?: number; fixed?: string | boolean;
  children?: AdaptiveColumn[]; orderFit?:boolean; fitFloor?:number;
}
export type ResolvedColumn<T> = T & { width:number; fixedWidth:number; rightOffset:number; layoutPlaceholder?:boolean; spacingWidth?:number }
// 2026-09-17：兼容现有Ant列数字字符串/px宽度；百分比交给容器分配，不能当NaN进入偏移。
const finite = (value:unknown):number|undefined => {
  const number=typeof value==='number'?value:typeof value==='string' && /^\d+(?:\.\d+)?(?:px)?$/.test(value.trim())?Number(value.trim().replace(/px$/,'')):NaN
  return Number.isFinite(number) && number>0?number:undefined
}
const field = (column:AdaptiveColumn):string => Array.isArray(column.dataIndex) ? column.dataIndex.join('.') : String(column.dataIndex || column.key || '')
const identity = (column:AdaptiveColumn):string => field(column)==='subcommand' ? 'command' : field(column)
const operation = (column:AdaptiveColumn):boolean => /^(command|subcommand|operate|operation|actions?|refound)$/i.test(field(column))
const rich = (column:AdaptiveColumn):number => /^(title|productName|orderInfo|eventName|description|content|adminRemark|remark|address|addressDetails)$/i.test(field(column)) ? 3
  : /(name|address|remark|description|businessLicense|orderNumber)$/i.test(field(column)) ? 1 : 0

function metrics(column:AdaptiveColumn, compact:boolean) {
  const legacy=compactColumnWidth({dataIndex:field(column),width:finite(column.width)})
  const declared=finite(column.width)
  const base=finite(column.minWidth) || (compact ? legacy : declared || legacy)
  const maximum=Math.max(base,Math.min(finite(column.maxWidth) || Infinity,column.orderFit ? base+24 : Infinity))
  const weight=typeof column.flex==='number' && Number.isFinite(column.flex) ? Math.max(0,column.flex)
    : column.fixed || operation(column) ? 0 : rich(column)
  // 订单列先保留各自可读基础宽度，额外留白平均分配给弹性列，避免宽屏名称/备注过度撑宽。
  return {base,maximum,weight:column.orderFit && weight>0 ? 1 : weight}
}

// 2026-09-17：默认表格恢复正式139的声明列宽和独立父子列，绝不按容器收缩/扩列或解除固定。
export function declaredTableColumns<T extends AdaptiveColumn>(columns:T[]) {
 let left=0,right=0
 const result=columns.map(column=>{
  const resolved={...column,fixedWidth:left,rightOffset:0}
  if(column.fixed==='left' || column.fixed===true)left+=finite(column.width) || 0
  return resolved
 })
 for(let index=result.length-1;index>=0;index--)if(result[index].fixed==='right'){result[index].rightOffset=right;right+=finite(result[index].width) || 0}
 return result
}

export function resolveColumnWidths<T extends AdaptiveColumn>(columns:T[], availableWidth:number, compact=true, gutter=0):ResolvedColumn<T>[] {
  const sizes=columns.map(column=>metrics(column,compact))
  const widths=sizes.map(size=>Math.ceil(size.base))
  // 订单专用：仅在声明的可读底线内收缩弹性列，金额/状态/日期/操作不被裁掉。
  if(columns.some(column=>column.orderFit) && availableWidth>0) {
    let deficit=Math.max(0,widths.reduce((sum,w)=>sum+w,0)+gutter-Math.floor(availableWidth))
    const floors=columns.map((column,index)=>Math.min(widths[index],Math.ceil(column.fitFloor || sizes[index].base)))
    // 订单长留言仅在放不下时先收至其他弹性文本的长度，短留言不额外缩减。
    // 宽屏仍按全文需要显示，不设固定留言宽度，也不改商品/客户列表的分配。
    const message=columns.findIndex(column=>column.orderFit && field(column)==='leaveMessage')
    if(deficit>0 && message>=0){
      const otherText=Math.max(floors[message],...widths.map((w,i)=>i!==message && sizes[i].weight>0?w:0))
      const cut=Math.min(deficit,Math.max(0,widths[message]-otherText))
      widths[message]-=cut;deficit-=cut
    }
    while(deficit>0) {
      const candidates=widths.map((_,i)=>i).filter(i=>widths[i]>floors[i])
      if(!candidates.length)break
      const capacity=candidates.reduce((sum,i)=>sum+widths[i]-floors[i],0),budget=deficit
      for(const i of candidates){const cut=Math.min(deficit,widths[i]-floors[i],Math.max(1,Math.floor(budget*(widths[i]-floors[i])/capacity)));widths[i]-=cut;deficit-=cut}
    }
  }
  const weights=sizes.map(size=>size.weight)
  // 没有长文本列时只选一个可伸展普通字段，操作列和显式flex=0列不被平均拉宽。
  if(!weights.some(Boolean)) {
    const fallback=columns.findIndex(column=>!column.fixed && !operation(column) && column.flex!==0)
    if(fallback>=0) weights[fallback]=1
  }
  let remaining=Math.max(0,Math.floor(availableWidth)-gutter-widths.reduce((sum,width)=>sum+width,0))
  let candidates=widths.map((_,index)=>index).filter(index=>weights[index]>0 && widths[index]<sizes[index].maximum)
  while(remaining>0 && candidates.length) {
    const total=candidates.reduce((sum,index)=>sum+weights[index],0),budget=remaining
    let used=0
    for(const index of candidates) {
      const extra=Math.min(remaining,Math.max(1,Math.floor(budget*weights[index]/total)),Math.floor(sizes[index].maximum-widths[index]))
      widths[index]+=extra;remaining-=extra;used+=extra
    }
    if(!used)break
    candidates=candidates.filter(index=>widths[index]<sizes[index].maximum)
  }
  const spacingWidths=widths.map(()=>0)
  // 全文字宽已满足后，不再把宽屏余量集中给名称/备注。额外间距均分至数据列，
  // 操作、图片与展开占位维持原宽度；各数据列新增留白相差不超过1像素。
  if(remaining>0 && columns.some(column=>column.orderFit)) {
    const spacing=columns.map((column,index)=>index).filter(index=>
      columns[index].orderFit && widths[index]>26 &&
      !/order-fit-action/.test(String((columns[index] as any).className || '')) &&
      !operation(columns[index]) && !/^(commands|delete|view|__omittedDetails)$/.test(field(columns[index])) &&
      !/(image|picture|thumbnail|img)/i.test(field(columns[index])))
    if(spacing.length){const extra=Math.floor(remaining/spacing.length),rest=remaining%spacing.length
      spacing.forEach((index,position)=>{spacingWidths[index]=extra+(position<rest?1:0);widths[index]+=spacingWidths[index]});remaining=0}
  }
  const resolved=columns.map((column,index)=>({...column,width:widths[index],spacingWidth:spacingWidths[index],fixedWidth:0,rightOffset:0})) as ResolvedColumn<T>[]
  let right=0
  for(let index=resolved.length-1;index>=0;index--)if(resolved[index].fixed==='right'){resolved[index].rightOffset=right;right+=widths[index]}
  const leftCandidates=resolved.map((column,index)=>index).filter(index=>resolved[index].fixed==='left' || resolved[index].fixed===true)
  // 优先保留可容纳的编号；极窄容器容不下编号和操作时取消左固定，所有数据仍可滚动访问。
  const anchor=leftCandidates.find(index=>/^(id|code|orderNumber|orderId|orgShortName)$/i.test(field(resolved[index]))) ?? leftCandidates[0]
  const leftBudget=availableWidth>0 ? Math.max(0,availableWidth-right-80-gutter) : Infinity
  const selected=new Set<number>()
  let used=0
  if(anchor!==undefined && widths[anchor]<=leftBudget){selected.add(anchor);used=widths[anchor]}
  for(const index of leftCandidates)if(!selected.has(index) && used+widths[index]<=leftBudget){selected.add(index);used+=widths[index]}
  let left=gutter
  for(let index=0;index<resolved.length;index++) {
    const column=resolved[index]
    if(column.fixed==='left' || column.fixed===true) {
      if(selected.has(index)){column.fixed='left';column.fixedWidth=left;left+=column.width}
      else column.fixed=undefined
    }
  }
  return resolved
}

export function resolveTableLayout<T extends AdaptiveColumn>(columns:T[], subColumns:T[], availableWidth:number, compact=true, gutter=0) {
  const slots:{main?:T;child?:T;model:T}[]=columns.map(column=>({main:column,model:column}))
  for(const child of subColumns) {
    const match=slots.find(slot=>!slot.child && identity(slot.model)===identity(child))
    if(match){match.child=child;const parent=match.model;match.model={...parent,minWidth:Math.max(metrics(parent,compact).base,metrics(child,compact).base)} as T}
    else {
      const right=slots.findIndex(slot=>slot.model.fixed==='right')
      slots.splice(right<0?slots.length:right,0,{child,model:child})
    }
  }
  const resolved=resolveColumnWidths(slots.map(slot=>slot.model),availableWidth,compact,gutter)
  const side=(name:'main'|'child')=>resolved.map((column,index)=>{
    const source=slots[index][name]
    return {...column,...(source || {title:'',dataIndex:'',slots:undefined}),key:source?.key || 'layout-gap-'+index,
      width:column.width,spacingWidth:column.spacingWidth,fixed:column.fixed,fixedWidth:column.fixedWidth,rightOffset:column.rightOffset,layoutPlaceholder:!source} as ResolvedColumn<T>
  })
  return {columns:side('main'),subColumns:side('child'),width:resolved.reduce((sum,column)=>sum+column.width,gutter)}
}

// 2026-09-17：Ant分组表头只分配叶子列，父分组宽度由同一批叶子相加，避免父子表头错位。
export function resolveGroupedColumns<T extends AdaptiveColumn>(columns:T[], availableWidth:number, compact=true, gutter=0) {
  const leaves:T[]=[]
  const collect=(items:T[],fixed?:string|boolean)=>items.forEach(column=>{
    const inherited=column.fixed ?? fixed
    if(column.children?.length)collect(column.children as T[],inherited)
    else leaves.push({...column,fixed:inherited})
  })
  collect(columns)
  const resolved=resolveColumnWidths(leaves,availableWidth,compact,gutter)
  let index=0
  const rebuild=(items:T[]):any[]=>items.map(column=>{
    if(!column.children?.length)return resolved[index++]
    const children=rebuild(column.children as T[])
    return {...column,children,width:children.reduce((sum,child)=>sum+child.width,0),
      fixed:children.every(child=>child.fixed===children[0].fixed)?children[0].fixed:undefined}
  })
  return {columns:rebuild(columns),width:resolved.reduce((sum,column)=>sum+column.width,gutter)}
}

// 仅显式fitColumns的清单使用。只复制列的显示元数据，不更改数据、插槽、过滤器或事件。
export function readableTableColumns<T extends AdaptiveColumn>(columns:T[]):T[] {
 return columns.map(column=>{
  if(column.children?.length)return {...column,children:readableTableColumns(column.children)} as T
  if(finite(column.minWidth))return {...column}
  const key=field(column),declared=finite(column.width)
  let minWidth=declared?Math.min(declared,160):88,flex=0,maxWidth:number|undefined=finite(column.maxWidth)
  if(operation(column) || /commands?$/i.test(key))minWidth=Math.max(160,declared || 0)
  else if(/(time|date|expiredAt|createdAt|updatedAt)$/i.test(key))minWidth=Math.max(160,declared || 0)
  else if(/(phone|mobile|tel)/i.test(key))minWidth=112
  else if(/(price|amount|balance|fee|money|total)/i.test(key))minWidth=104
  else if(/^(orderNumber|orderId)$/i.test(key))minWidth=190
  else if(/(Info|logistics)$/i.test(key))minWidth=Math.max(180,declared || 0)
  else if(/(image|picture|thumbnail|img)/i.test(key))minWidth=declared || 56
  else if(/(status|state|type)/i.test(key))minWidth=Math.max(88,Math.min(declared || 88,160))
  else if(/(title|productName|message|remark|description|content|reason)/i.test(key)){minWidth=96;flex=3}
  else if(/name$/i.test(key)){minWidth=96;flex=1;maxWidth=200}
  else if(declared && declared>=240)minWidth=declared
  return {...column,minWidth,flex,maxWidth} as T
 })
}
